import type { Annotation } from "@rapid/types/annotation";
import type { Prediction } from "@rapid/utils/api/inference";

type Box = { x: number; y: number; width: number; height: number };

/**
 * Calculate Intersection over Union (IoU) between two boxes.
 * Boxes are expected to have x, y at center with width and height.
 */
export function calculateIoU(boxA: Box, boxB: Box): number {
    // Convert center-based coordinates to corner-based
    const boxALeft = boxA.x - boxA.width / 2;
    const boxARight = boxA.x + boxA.width / 2;
    const boxATop = boxA.y - boxA.height / 2;
    const boxABottom = boxA.y + boxA.height / 2;

    const boxBLeft = boxB.x - boxB.width / 2;
    const boxBRight = boxB.x + boxB.width / 2;
    const boxBTop = boxB.y - boxB.height / 2;
    const boxBBottom = boxB.y + boxB.height / 2;

    // Calculate intersection
    const intersectLeft = Math.max(boxALeft, boxBLeft);
    const intersectRight = Math.min(boxARight, boxBRight);
    const intersectTop = Math.max(boxATop, boxBTop);
    const intersectBottom = Math.min(boxABottom, boxBBottom);

    const intersectWidth = Math.max(0, intersectRight - intersectLeft);
    const intersectHeight = Math.max(0, intersectBottom - intersectTop);
    const intersectArea = intersectWidth * intersectHeight;

    // Calculate union
    const boxAArea = boxA.width * boxA.height;
    const boxBArea = boxB.width * boxB.height;
    const unionArea = boxAArea + boxBArea - intersectArea;

    if (unionArea === 0) return 0;

    return intersectArea / unionArea;
}

/**
 * Calculate Intersection over Minimum area (IoMin) between two boxes.
 * This is useful when you want to detect if a smaller box is mostly contained within a larger one.
 */
export function calculateIoMin(boxA: Box, boxB: Box): number {
    // Convert center-based coordinates to corner-based
    const boxALeft = boxA.x - boxA.width / 2;
    const boxARight = boxA.x + boxA.width / 2;
    const boxATop = boxA.y - boxA.height / 2;
    const boxABottom = boxA.y + boxA.height / 2;

    const boxBLeft = boxB.x - boxB.width / 2;
    const boxBRight = boxB.x + boxB.width / 2;
    const boxBTop = boxB.y - boxB.height / 2;
    const boxBBottom = boxB.y + boxB.height / 2;

    // Calculate intersection
    const intersectLeft = Math.max(boxALeft, boxBLeft);
    const intersectRight = Math.min(boxARight, boxBRight);
    const intersectTop = Math.max(boxATop, boxBTop);
    const intersectBottom = Math.min(boxABottom, boxBBottom);

    const intersectWidth = Math.max(0, intersectRight - intersectLeft);
    const intersectHeight = Math.max(0, intersectBottom - intersectTop);
    const intersectArea = intersectWidth * intersectHeight;

    // Calculate minimum area
    const boxAArea = boxA.width * boxA.height;
    const boxBArea = boxB.width * boxB.height;
    const minArea = Math.min(boxAArea, boxBArea);

    if (minArea === 0) return 0;

    return intersectArea / minArea;
}

/**
 * Filter predictions that overlap with existing annotations using NMS.
 * Returns predictions that don't significantly overlap with existing annotations.
 *
 * @param predictions - New predictions to filter
 * @param existingAnnotations - Existing annotations to compare against
 * @param iouThreshold - IoU threshold above which a prediction is considered a duplicate (default 0.5)
 * @param labelFilter - Optional label to filter predictions by (only dedupe against annotations with same label)
 */
export function deduplicatePredictionsAgainstAnnotations<T extends Box & { class?: string }>(
    predictions: T[],
    existingAnnotations: (Annotation & Box)[],
    iouThreshold: number = 0.5,
    labelFilter?: string
): T[] {
    // Filter annotations by label if specified
    const relevantAnnotations = labelFilter
        ? existingAnnotations.filter((anno) => anno.label === labelFilter)
        : existingAnnotations;

    if (relevantAnnotations.length === 0) {
        return predictions;
    }

    return predictions.filter((prediction) => {
        // Check if this prediction overlaps significantly with any existing annotation
        for (const annotation of relevantAnnotations) {
            // If labelFilter is specified, only compare against same-label annotations
            // If no labelFilter, compare prediction class against annotation label
            if (!labelFilter && prediction.class && prediction.class !== annotation.label) {
                continue;
            }

            const iou = calculateIoU(prediction, annotation);
            if (iou >= iouThreshold) {
                // This prediction overlaps with an existing annotation, filter it out
                return false;
            }
        }
        // No significant overlap found, keep this prediction
        return true;
    });
}

/**
 * Standard NMS within a set of predictions.
 * Sorts by confidence and removes overlapping lower-confidence predictions.
 *
 * @param predictions - Predictions to apply NMS to
 * @param iouThreshold - IoU threshold above which predictions are considered duplicates
 */
export function applyNMS<T extends Box & { confidence: number }>(
    predictions: T[],
    iouThreshold: number = 0.5
): T[] {
    if (predictions.length === 0) return [];

    // Sort by confidence (descending)
    const sorted = [...predictions].sort((a, b) => b.confidence - a.confidence);
    const kept: T[] = [];

    for (const prediction of sorted) {
        let shouldKeep = true;

        for (const keptPrediction of kept) {
            const iou = calculateIoU(prediction, keptPrediction);
            if (iou >= iouThreshold) {
                shouldKeep = false;
                break;
            }
        }

        if (shouldKeep) {
            kept.push(prediction);
        }
    }

    return kept;
}

/**
 * Cross-class NMS that deduplicates new predictions against existing annotations,
 * comparing confidences to keep only the higher confidence detection.
 *
 * This is useful when different classes can detect the same object (e.g., "white team player"
 * and "yellow team player" both detecting the same person). Only the higher confidence
 * detection should be kept.
 *
 * @param newPredictions - New predictions to filter (must have confidence)
 * @param existingAnnotations - Existing annotations to compare against
 * @param iouThreshold - IoU threshold above which boxes are considered the same object
 * @returns Object with:
 *   - keptPredictions: new predictions that should be added
 *   - annotationsToRemove: IDs of existing annotations that should be removed (lower confidence)
 */
export function crossClassNMSWithExisting<T extends Box & { confidence: number; label?: string }>(
    newPredictions: T[],
    existingAnnotations: (Annotation & Box & { confidence?: number })[],
    iouThreshold: number = 0.5
): { keptPredictions: T[]; annotationIdsToRemove: string[] } {
    if (newPredictions.length === 0) {
        return { keptPredictions: [], annotationIdsToRemove: [] };
    }

    if (existingAnnotations.length === 0) {
        return { keptPredictions: newPredictions, annotationIdsToRemove: [] };
    }

    const keptPredictions: T[] = [];
    const annotationIdsToRemove: string[] = [];
    // Track which existing annotations have already been "claimed" by a higher-confidence prediction
    const claimedAnnotationIds = new Set<string>();

    // Sort new predictions by confidence (highest first)
    const sortedPredictions = [...newPredictions].sort((a, b) => b.confidence - a.confidence);

    for (const prediction of sortedPredictions) {
        let shouldKeepPrediction = true;
        let overlappingAnnotation: (Annotation & Box & { confidence?: number }) | null = null;

        // Check against all existing annotations (cross-class)
        for (const annotation of existingAnnotations) {
            // Skip annotations that have already been marked for removal
            if (claimedAnnotationIds.has(annotation.id)) {
                continue;
            }

            const iou = calculateIoU(prediction, annotation);
            if (iou >= iouThreshold) {
                overlappingAnnotation = annotation;
                break;
            }
        }

        if (overlappingAnnotation) {
            // There's an overlapping annotation - compare confidences
            const existingConfidence = overlappingAnnotation.confidence ?? 0;

            if (prediction.confidence > existingConfidence) {
                // New prediction has higher confidence - keep it and remove existing
                shouldKeepPrediction = true;
                annotationIdsToRemove.push(overlappingAnnotation.id);
                claimedAnnotationIds.add(overlappingAnnotation.id);
            } else {
                // Existing annotation has higher or equal confidence - discard new prediction
                shouldKeepPrediction = false;
            }
        }

        if (shouldKeepPrediction) {
            // Also check against already-kept predictions (standard NMS within new predictions)
            for (const keptPred of keptPredictions) {
                const iou = calculateIoU(prediction, keptPred);
                if (iou >= iouThreshold) {
                    shouldKeepPrediction = false;
                    break;
                }
            }
        }

        if (shouldKeepPrediction) {
            keptPredictions.push(prediction);
        }
    }

    return { keptPredictions, annotationIdsToRemove };
}
